/**
 * How the Map is shown (ADR-100's amendment of 2026-10-14, decisions 48, 49, 50 and 52), pure: the framed window, the
 * float, or the map popped out into a window of its own, one at a time. Which of window and float an open takes is
 * kept on the device; a popped out map is never kept, since a window needs a gesture to open (decision 34). The phone
 * layout has the window alone.
 */

/**
 * A way of showing the Map.
 */
export type MapPresentation = "window" | "float" | "popped";

/**
 * The two ways a person chooses between and the device keeps.
 */
export type KeptPresentation = "window" | "float";

/**
 * Where the Map's presentation stands: what is shown, and what the next open shows.
 */
export interface PresentationState {
  readonly shown: MapPresentation;
  readonly kept: KeptPresentation;
}

/**
 * Where the kept presentation lives on the device: `window` or `float`.
 */
export const PRESENTATION_KEY = "odyssey.map.presentation";

/**
 * Reads the kept presentation.
 *
 * @param raw The kept text, or null when nothing is kept.
 * @returns `float` for the text "float" and `window` for anything else.
 */
export function parsePresentation(raw: string | null): KeptPresentation {
  return raw === "float" ? "float" : "window";
}

/**
 * The text a kept presentation is written as.
 *
 * @param kept The presentation.
 * @returns "window" or "float".
 */
export function formatPresentation(kept: KeptPresentation): string {
  return kept;
}

/**
 * The state of a Map just opened: the kept presentation, shown.
 *
 * @param kept The presentation kept on this device.
 * @returns The state.
 */
export function openedState(kept: KeptPresentation): PresentationState {
  return { shown: kept, kept };
}

/**
 * The state after Float: the float is shown and is what the next open shows.
 *
 * @param _state The state before.
 * @returns The state.
 */
export function floatedState(_state: PresentationState): PresentationState {
  return { shown: "float", kept: "float" };
}

/**
 * The state after Dock: the window is shown and is what the next open shows.
 *
 * @param _state The state before.
 * @returns The state.
 */
export function dockedState(_state: PresentationState): PresentationState {
  return { shown: "window", kept: "window" };
}

/**
 * The state after Pop out: the popped out map is shown, and what M last opened is remembered for Pop in.
 *
 * @param state The state before.
 * @returns The state.
 */
export function poppedOutState(state: PresentationState): PresentationState {
  return { shown: "popped", kept: state.kept };
}

/**
 * The state after Pop in, or after the popped out window is closed: the presentation last used.
 *
 * @param state The state before.
 * @returns The state.
 */
export function poppedInState(state: PresentationState): PresentationState {
  return { shown: state.kept, kept: state.kept };
}

/**
 * What a screen shows for a state: the phone layout has the window alone, so a float is drawn as the window there and
 * comes back as the float where the screen grows, while a popped out map is in another window whatever the screen is.
 *
 * @param shown What the state says is shown.
 * @param phone Whether the screen is the phone layout.
 * @returns The presentation to draw.
 */
export function shownPresentation(shown: MapPresentation, phone: boolean): MapPresentation {
  return phone && shown === "float" ? "window" : shown;
}
